import React, { useEffect, useMemo, useState } from "react";
import { createApi } from "../../../../core/api";
import { Table } from "../../../components/SystemComponent/table";
import { Button } from "../../../components/SystemComponent/Button";
import { Modal } from "../../../components/SystemComponent/Modal";
import { MdAdd, MdClose, MdCheck, MdUpload, MdVisibility } from "react-icons/md";

export const InvoicePage = () => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  // Optional: Client-side filter could be added, but listing all for now as per "list invoices" instruction.
  
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const BASE_FILE_URL = import.meta.env.VITE_API_URL || (import.meta.env.VITE_API_BASE_URL ? import.meta.env.VITE_API_BASE_URL.replace(/\/api\/?$/, "") : "https://api-test.fixeraviation.com");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.dashboard.getInvoices();
      if (res.ok) {
        // Handle wrapped response { data: [...] } 
        // res.data might be the wrapper object, so check if res.data.data is the array
        const resultData = (res.data as any).data || res.data;
        setData(Array.isArray(resultData) ? resultData : []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUploadClick = React.useCallback((item: any) => {
    setSelectedItem(item);
    setInvoiceNumber(item.invoiceNumber || ""); 
    setOpenUpload(true);
  }, []);

  const handleUpload = React.useCallback(async () => {
    if (!file || !invoiceNumber || !selectedItem) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("InvoiceId", selectedItem.id); 
      formData.append("InvoiceNumber", invoiceNumber);
      
      // userId is not in the provided JSON snippet, but maybe it's there hidden or we don't need it?
      // The snippet shows 'userFullNameSnapshot' but not 'userId'. 
      // If the backend NEEDS userId, we might have a problem if it's not in the response.
      // However, usually invoice upload might only need InvoiceId. 
      // I will check if 'userId' exists in the row just in case, using camelCase.
      if (selectedItem.userId) {
        formData.append("UserId", selectedItem.userId);
      }

      formData.append("File", file);
      
      const res = await api.dashboard.sendInvoice(formData);
      if (res.ok) {
        setOpenUpload(false);
        setInvoiceNumber("");
        setFile(null);
        setSelectedItem(null);
        load();
      } else {
        alert("Failed to upload invoice");
      }
    } catch (e) {
      alert("Error uploading invoice");
    } finally {
      setUploading(false);
    }
  }, [file, invoiceNumber, selectedItem, api, load]);

  const getFileUrl = React.useCallback((r: any) => {
    if (!r) return undefined;
    let url: string | undefined = undefined;

    // Check for nested invoiceFile object with url property
    if (r.invoiceFile && typeof r.invoiceFile === 'object' && r.invoiceFile.url) {
        url = r.invoiceFile.url;
    } 
    // Check if invoiceFile is a direct string
    else if (typeof r.invoiceFile === 'string') {
        url = r.invoiceFile;
    } 
    // Fallback to other possible keys
    else if (typeof r.fileUrl === 'string') {
        url = r.fileUrl;
    } 
    else if (typeof r.invoiceFileUrl === 'string') {
        url = r.invoiceFileUrl;
    }

    if (!url) return undefined;

    if (url.startsWith("http")) return url;
    return `${BASE_FILE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
  }, [BASE_FILE_URL]);

  const columns = useMemo(() => [
    { key: "id", title: "ID", accessor: (r: any) => r?.id },
    { key: "invoiceNumber", title: "Invoice Number", accessor: (r: any) => r?.invoiceNumber || "-" },
    { key: "invoiceDate", title: "Date", accessor: (r: any) => r?.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString() : "-" },
    { key: "user", title: "User", accessor: (r: any) => r?.userFullNameSnapshot || "-" },
    { key: "company", title: "Company", accessor: (r: any) => r?.companyNameSnapshot || "-" },
    { key: "amount", title: "Amount", accessor: (r: any) => r?.amount != null ? `${Number(r.amount).toFixed(2)} ${r.currency || ""}` : "-" },
    { key: "status", title: "Status", accessor: (r: any) => r?.status === 1 ? "Paid" : "Unpaid" },
    {
      key: "actions",
      title: "Actions",
      render: (r: any) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="sm" onClick={() => handleUploadClick(r)}>
            Upload
          </Button>
          {getFileUrl(r) && (
            <Button 
               size="sm" 
               variant="ghost" 
               onClick={() => {
                  setSelectedItem(r);
                  setInvoiceNumber(r.invoiceNumber || "");
                  setOpenUpload(true);
               }}
               title="View & Edit Invoice"
            >
               <MdVisibility size={18} />
            </Button>
          )}
        </div>
      )
    }
  ], [handleUploadClick, getFileUrl]);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
         <h2 style={{ fontSize: 20, fontWeight: 700 }}>Invoices</h2>
         {/* Top upload button removed as per request */}
      </div>

      <Table loading={loading} columns={columns} data={data} />

      {openUpload && (
        <Modal open onClose={() => setOpenUpload(false)}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>
             {selectedItem && getFileUrl(selectedItem) ? "View & Update Invoice" : "Upload Invoice"}
          </div>
          
          {selectedItem && getFileUrl(selectedItem) && (
            <div style={{ marginBottom: 16 }}>
               <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B", marginBottom: 8 }}>Current Document:</div>
               {(() => {
                   const url = getFileUrl(selectedItem)!;
                   const isImage = /\.(jpeg|jpg|png|gif|webp)$/i.test(url);
                   
                   if (isImage) {
                       return (
                           <div style={{ padding: 10, background: "#f1f5f9", borderRadius: 8, textAlign: "center" }}>
                               <img src={url} alt="Invoice" style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain" }} />
                               <div style={{ marginTop: 8 }}>
                                   <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#2563EB" }}>Open Full Size</a>
                               </div>
                           </div>
                       );
                   } else {
                       // Assume PDF or other document type that browser handles nicely in IFRAME
                       return (
                           <div style={{ height: 350,  background: "#f1f5f9", borderRadius: 8, overflow: "hidden" }}>
                               <iframe src={url} style={{ width: "100%", height: "100%", border: "none" }} title="Document Preview" />
                           </div>
                       );
                   }
               })()}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Invoice Number</label>
            <input 
              value={invoiceNumber}
              onChange={e => setInvoiceNumber(e.target.value)}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #CBD5E1" }}
              placeholder="Enter invoice number..."
            />
          </div>

          <div style={{ marginBottom: 12 }}>
             <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Invoice File</label>
             <input 
               type="file" 
               accept=".pdf,image/png,image/jpeg,image/jpg"
               onChange={e => {
                 const file = e.target.files ? e.target.files[0] : null;
                 if (file) {
                    if (file.type === "application/pdf" || file.type.startsWith("image/")) {
                        setFile(file);
                    } else {
                        alert("Only PDF and Image files are allowed.");
                        e.target.value = "";
                        setFile(null);
                    }
                 } else {
                    setFile(null);
                 }
               }}
               style={{ width: "100%" }} 
             />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
             <Button onClick={handleUpload} disabled={uploading}>
               {uploading ? "Uploading..." : "Upload"}
             </Button>
             <Button variant="ghost" onClick={() => setOpenUpload(false)} disabled={uploading}>Cancel</Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default InvoicePage;
